import React from "react";

// Model Layout Component - Trends
const playBounce = (event, state) => {
  const el = event.currentTarget;
  el.classList.remove("map-hover-enter", "map-hover-leave");
  void el.offsetWidth;
  el.classList.add(state);
};

const bounceHandlers = {
  onMouseEnter: (e) => playBounce(e, "map-hover-enter"),
  onMouseLeave: (e) => playBounce(e, "map-hover-leave"),
};

const trendLayers = [
  {
    id: "ts1",
    src: "/assets/svgs/trends-svgs/ts1.svg",
    alt: "Ageing population bars",
    style: { left: "5%", top: "17%" },
  },
  {
    id: "ts2",
    src: "/assets/svgs/trends-svgs/ts2.svg",
    alt: "Green care bars",
    style: { left: "16%", top: "23%" },
  },
  {
    id: "ts3",
    src: "/assets/svgs/trends-svgs/ts3.svg",
    alt: "Assistive devices illustration",
    style: { left: "10%", top: "36%" },
  },
  {
    id: "ts4",
    src: "/assets/svgs/trends-svgs/ts4.svg",
    alt: "Pink care bars",
    style: { left: "24%", top: "35%" },
  },
  {
    id: "ts5",
    src: "/assets/svgs/trends-svgs/ts5.svg",
    alt: "Growing ageing population label",
    style: { left: "33%", top: "12%" },
  },
  {
    id: "ts6",
    src: "/assets/svgs/trends-svgs/ts6.svg",
    alt: "Fertility and births trend",
    style: { left: "37%", top: "28%" },
    static: true,
  },
  {
    id: "ts7",
    src: "/assets/svgs/trends-svgs/ts7.svg",
    alt: "Population change metric 1",
    style: { left: "33.5%", top: "31%" },
  },
  {
    id: "ts8",
    src: "/assets/svgs/trends-svgs/ts8.svg",
    alt: "Population change metric 2",
    style: { left: "38%", top: "28%" },
  },
  {
    id: "ts9",
    src: "/assets/svgs/trends-svgs/ts9.svg",
    alt: "Population change metric 3",
    style: { left: "41.5%", top: "25.5%" },
  },
  {
    id: "ts10",
    src: "/assets/svgs/trends-svgs/ts10.svg",
    alt: "Central platform connector",
    style: { left: "45%", top: "23%" },
  },
  {
    id: "ts11",
    src: "/assets/svgs/trends-svgs/ts11.svg",
    alt: "Trends core stack",
    style: { left: "49%", top: "43%" },
    static: true,
  },
  {
    id: "ts12",
    src: "/assets/svgs/trends-svgs/ts12.svg",
    alt: "Decline in paid caregiving",
    style: { left: "70%", top: "31%" },
    tooltips: ["Trends x Care Economy", "Trends x Gender"],
    tooltipPlacement: "top",
  },
  {
    id: "ts13",
    src: "/assets/svgs/trends-svgs/ts13.svg",
    alt: "Caregiver centered models",
    style: { left: "82.9%", top: "62.5%" },
  },
  {
    id: "ts14",
    src: "/assets/svgs/trends-svgs/ts14.svg",
    alt: "Technology and caregiving",
    style: { left: "67%", top: "74.8%" },
  },
  {
    id: "ts15",
    src: "/assets/svgs/trends-svgs/ts15.svg",
    alt: "Changing family structures",
    style: { left: "35.2%", top: "50.9%" },
    tooltips: ["Trends x Mental Health"],
    tooltipPlacement: "right",
  },
  {
    id: "ts16",
    src: "/assets/svgs/trends-svgs/ts16.svg",
    alt: "Migration and remittances",
    style: { left: "31%", top: "55%" },
    static: true,
  },
  {
    id: "ts17",
    src: "/assets/svgs/trends-svgs/ts17.svg",
    alt: "Remote caregiving pathway",
    style: { left: "12%", top: "64%" },
  },
  {
    id: "ts18",
    src: "/assets/svgs/trends-svgs/ts18.svg",
    alt: "Multigenerational caregiving",
    style: { left: "40%", top: "79.5%" },
  },
];

const TrendsModel = ({ className = "", showTooltips = true }) => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const getScrollTarget = (layerId) => {
    const scrollMap = {
      ts1: "india-demographics",
      ts2: "bangladesh-demographics",
      ts3: "nepal-demographics",
      ts4: "indonesia-demographics",
      ts5: "growing-aging-population",
      ts7: "india-demographics",
      ts8: "bangladesh-demographics",
      ts9: "indonesia-demographics",
      ts10: "nepal-demographics",
      ts12: "decline-paid-care-workforce",
      ts13: "person-centered-care-caregiver-centered-care",
      ts14: "technology-supporting-caregiving",
      ts15: "changing-family-structures",
      ts17: "increase-long-distance-caregiving",
      ts18: "caregiving-multigenerational-households",
    };
    return scrollMap[layerId];
  };

  return (
    <main className="relative pt-5">
      <section className="p-2">
        <div
          className={`relative mx-auto aspect-[1070/865] max-w-[1100px] ${className}`}
        >
          {trendLayers.map((layer) => {
            const interactive = !layer.static;
            const hoverTiming = {
              "--map-seq-name": "mapSeqReveal",
              "--map-seq-duration": "1200ms",
              "--map-seq-delay": "0ms",
            };

            const tooltipPos = (() => {
              switch (layer.tooltipPlacement) {
                case "left":
                  return "left-full top-1/2 -translate-y-1/2 ml-2";
                case "right":
                  return "right-full top-[14%] -translate-y-1/2 mr-2";
                case "top":
                  return "bottom-[80%] left-[75%] -translate-x-1/2 -mt-2";
                default:
                  return "top-full left-1/2 -translate-x-1/2 mt-2";
              }
            })();

            const scrollTarget = getScrollTarget(layer.id);
            const handleClick = scrollTarget
              ? () => scrollToSection(scrollTarget)
              : undefined;

            return (
              <div
                key={layer.id}
                className={`absolute -translate-x-1/2 -translate-y-1/2 ${interactive ? "group" : ""
                  }`}
                style={layer.style}
              >
                <div className="relative">
                  <img
                    src={layer.src}
                    alt={layer.alt}
                    // loading="lazy"
                    className={`select-none max-w-max object-contain ${interactive
                        ? "map-hover-bounce hover:cursor-pointer pointer-events-auto map-seq"
                        : "pointer-events-none"
                      }`}
                    style={interactive ? { ...hoverTiming } : undefined}
                    {...(interactive ? bounceHandlers : {})}
                    {...(handleClick ? { onClick: handleClick } : {})}
                  />
                  {interactive &&
                    showTooltips &&
                    Array.isArray(layer.tooltips) &&
                    layer.tooltips.map((tip, idx) => {
                      const offset = idx * 44;
                      const placement = layer.tooltipPlacement;
                      const transform =
                        placement === "left" || placement === "right"
                          ? `translateY(calc(-50% + ${offset}px))`
                          : `translateX(-50%) translateY(${offset}px)`;

                      return (
                        <span
                          key={`${layer.id}-tip-${idx}`}
                          className={`pointer-events-none z-50 absolute rounded-md bg-[#FEFBF6] p-2 text-[11px] font-normal text-black text-nowrap opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${tooltipPos}`}
                          style={{ transform }}
                        >
                          {tip}
                        </span>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default TrendsModel;
